/**
 * SectionHeader component
 * Provides a standardized section heading with optional eyebrow and subtitle
 * Increased default title size for better prominence
 */

import React from 'react'

/** Props for SectionHeader component */
export interface SectionHeaderProps {
  /** Optional small label above the title */
  eyebrow?: string
  /** Main section title */
  title: string
  /** Optional supporting text below the title */
  subtitle?: string
  /** Text alignment for the block */
  align?: 'left' | 'center'
  /** Extra class names to customize spacing or layout */
  className?: string
}

/** Section header block with consistent spacing and typographic scale */
export default function SectionHeader({
  eyebrow,
  title,
  subtitle,
  align = 'center',
  className = '',
}: SectionHeaderProps): JSX.Element {
  return (
    <div className={`w-full ${align === 'center' ? 'text-center' : 'text-left'} ${className}`}>
      {eyebrow && (
        <div className="inline-block text-xs font-semibold tracking-wider uppercase text-orange-600 bg-orange-50 border border-orange-200 px-3 py-1 rounded-full">
          {eyebrow}
        </div>
      )}
      {/* Increased from text-3xl/sm:text-4xl to text-4xl/sm:text-5xl */}
      <h2 className={`mt-3 text-4xl sm:text-5xl font-extrabold text-gray-900`}>
        {title}
      </h2>
      {subtitle && (
        <p className={`mt-3 text-gray-600 max-w-3xl ${align === 'center' ? 'mx-auto' : ''}`}>
          {subtitle}
        </p>
      )}
    </div>
  )
}
